<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { ECommandPalette, useTheme, type Command } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { listTenants } from '@/api/admin'
import { isAppLocale, LOCALE_NAMES, setLocale, SUPPORTED_LOCALES } from '@/i18n'
import { useAuthStore } from '@/stores/auth'
import { listKey } from '@/lib/queryKeys'

const open = defineModel<boolean>('open', { default: false })

const router = useRouter()
const auth = useAuthStore()
const { t, locale } = useI18n()
const { setTheme, resolved } = useTheme()

/**
 * Tenants are in the palette because "find the tenant that just called" is the
 * single most common thing an operator does. The list is only fetched while
 * the palette is open, and it is the same cursor-paginated endpoint the list
 * screen uses — the palette shows the first page and the list screen is one
 * command away for anything beyond it.
 */
const tenantsQuery = useQuery({
  queryKey: listKey('palette-tenants', {}),
  queryFn: () => listTenants({ limit: 50 }),
  enabled: open,
  staleTime: 60_000,
})

const commands = computed<Command[]>(() => {
  const goTo = t('palette.groups.goTo')

  const navigation: Command[] = [
    { id: 'nav:dashboard', label: t('palette.items.dashboard'), group: goTo, icon: 'info' },
    { id: 'nav:tenants', label: t('palette.items.tenants'), group: goTo, icon: 'inbox' },
    { id: 'nav:plans', label: t('palette.items.plans'), group: goTo, icon: 'columns' },
    { id: 'nav:admins', label: t('palette.items.admins'), group: goTo, icon: 'check' },
  ]

  const actions: Command[] = [
    {
      id: 'action:onboard',
      label: t('palette.items.onboard'),
      group: t('palette.groups.create'),
      icon: 'plus',
      description: t('palette.items.onboardHint'),
      disabled: !auth.canSupport,
    },
    {
      id: 'action:theme',
      label:
        resolved.value === 'dark'
          ? t('palette.items.themeToLight')
          : t('palette.items.themeToDark'),
      group: t('palette.groups.preferences'),
      icon: 'refresh',
    },
    // A language is only worth offering while it is not the one in force.
    ...SUPPORTED_LOCALES.filter((value) => value !== locale.value).map<Command>((value) => ({
      id: `action:locale:${value}`,
      label: t('palette.items.localeSwitch', { language: LOCALE_NAMES[value] }),
      group: t('palette.groups.preferences'),
      icon: 'refresh',
      keywords: [LOCALE_NAMES[value], value],
    })),
  ]

  const tenants: Command[] = (tenantsQuery.data.value?.items ?? []).map((tenant) => ({
    id: `tenant:${tenant.id}`,
    label: tenant.name,
    group: t('palette.groups.tenants'),
    hint: tenant.slug,
    keywords: [tenant.slug, tenant.status, tenant.region],
    icon: 'inbox',
  }))

  return [...navigation, ...actions, ...tenants]
})

async function onSelect(command: Command): Promise<void> {
  open.value = false
  if (command.id === 'nav:dashboard') await router.push({ name: 'dashboard' })
  else if (command.id === 'nav:tenants') await router.push({ name: 'tenants' })
  else if (command.id === 'nav:plans') await router.push({ name: 'plans' })
  else if (command.id === 'nav:admins') await router.push({ name: 'admins' })
  else if (command.id === 'action:onboard') await router.push({ name: 'tenant-create' })
  else if (command.id === 'action:theme') setTheme(resolved.value === 'dark' ? 'light' : 'dark')
  else if (command.id.startsWith('action:locale:')) {
    const next = command.id.slice('action:locale:'.length)
    if (isAppLocale(next)) setLocale(next)
  } else if (command.id.startsWith('tenant:')) {
    await router.push({ name: 'tenant-detail', params: { id: command.id.slice('tenant:'.length) } })
  }
}
</script>

<template>
  <ECommandPalette
    v-model:open="open"
    :commands="commands"
    :placeholder="$t('palette.placeholder')"
    :empty-text="$t('palette.empty')"
    @select="onSelect"
  />
</template>
